/**
 * Day Book Screen - Routes through .NET API proxy (/api/finance/daybook)
 * with MSSQL fallback. Shows daily voucher transactions.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { BookOpen, Calendar, Filter, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { ReportLayout } from '@/components/report-engine';
import { API_BASE as API_URL, getToken } from '../../lib/api';

const hdrs = () => ({ Authorization: `Bearer ${getToken() || ''}`, 'Content-Type': 'application/json' });

const DayBookScreen = ({ theme }) => {
  const isDark = theme === 'dark';
  
  // Data state
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataSource, setDataSource] = useState('');
  
  // Filter state
  const [fromDate, setFromDate] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`; });
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [voucherTypeFilter, setVoucherTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Summary state
  const [totalDebit, setTotalDebit] = useState(0);
  const [totalCredit, setTotalCredit] = useState(0);
  const [voucherCounts, setVoucherCounts] = useState({
    cashReceipt: 0,
    cashPayment: 0,
    bankReceipt: 0,
    bankPayment: 0
  });

  useEffect(() => {
    fetchDayBook();
  }, [fromDate, toDate, page, pageSize]);

  const fetchDayBook = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use .NET proxy endpoint (falls back to MSSQL automatically)
      const params = new URLSearchParams({ fromDate, toDate, page, pageSize });
      const r = await fetch(`${API_URL}/api/finance/daybook?${params}`, { headers: hdrs() });
      const j = await r.json();

      if (j.success) {
        const rows = j.data || [];
        setData(rows);
        setDataSource(j.source || '');
        setTotalRows(j.total || rows.length);
        setTotalPages(Math.ceil((j.total || rows.length) / pageSize));

        // Calculate totals
        const totDebit = rows.reduce((sum, row) => sum + (parseFloat(row.DrAmount || row.drAmount) || 0), 0);
        const totCredit = rows.reduce((sum, row) => sum + (parseFloat(row.CrAmount || row.crAmount) || 0), 0);
        setTotalDebit(totDebit);
        setTotalCredit(totCredit);

        // Count vouchers by type
        const counts = {
          cashReceipt: rows.filter(r => (r.VoucherType || r.voucherType || '').toLowerCase().includes('cash receipt')).length,
          cashPayment: rows.filter(r => (r.VoucherType || r.voucherType || '').toLowerCase().includes('cash payment')).length,
          bankReceipt: rows.filter(r => (r.VoucherType || r.voucherType || '').toLowerCase().includes('bank receipt')).length,
          bankPayment: rows.filter(r => (r.VoucherType || r.voucherType || '').toLowerCase().includes('bank payment')).length
        };
        setVoucherCounts(counts);
      } else {
        setError(j.error || 'Failed to load day book data');
      }
    } catch (err) {
      console.error('Error fetching day book:', err);
      setError('Failed to load day book data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = useCallback(() => {
    fetchDayBook();
  }, [fromDate, toDate, page, pageSize]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  // Filter data — handle both PascalCase (MSSQL) and camelCase (.NET) field names
  const filteredData = data.map(row => ({
    DocId: row.DocId || row.docId || '',
    DocDate: row.DocDate || row.docDate || '',
    VoucherType: row.VoucherType || row.voucherType || '',
    AccountName: row.AccountName || row.accountName || '',
    DrAmount: row.DrAmount || row.drAmount || 0,
    CrAmount: row.CrAmount || row.crAmount || 0,
    Narration: row.Narration || row.narration || '',
  })).filter(row => {
    if (voucherTypeFilter !== 'all') {
      if (voucherTypeFilter === 'cash' && !row.VoucherType?.toLowerCase().includes('cash')) return false;
      if (voucherTypeFilter === 'bank' && !row.VoucherType?.toLowerCase().includes('bank')) return false;
      if (voucherTypeFilter === 'receipt' && !row.VoucherType?.toLowerCase().includes('receipt')) return false;
      if (voucherTypeFilter === 'payment' && !row.VoucherType?.toLowerCase().includes('payment')) return false;
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        row.DocId?.toLowerCase().includes(q) ||
        row.AccountName?.toLowerCase().includes(q) ||
        row.Narration?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  // Summary cards
  const summaryCards = [
    { 
      label: 'Total Vouchers', 
      value: data.length.toString(),
      icon: <BookOpen size={16} />
    },
    { 
      label: 'Total Debit', 
      value: formatCurrency(totalDebit), 
      variant: 'success',
      icon: <ArrowUpRight size={16} />
    },
    { 
      label: 'Total Credit', 
      value: formatCurrency(totalCredit), 
      variant: 'danger',
      icon: <ArrowDownRight size={16} />
    },
    { 
      label: 'Cash/Bank', 
      value: `${voucherCounts.cashReceipt + voucherCounts.cashPayment} / ${voucherCounts.bankReceipt + voucherCounts.bankPayment}`
    }
  ];

  // Toolbar filters
  const FiltersToolbar = (
    <div className="flex flex-wrap items-center gap-3">
      {/* Date Range */}
      <div className="flex items-center gap-2">
        <Calendar size={16} className={isDark ? 'text-slate-500' : 'text-gray-400'} />
        <span className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>From</span>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          data-testid="daybook-from-date"
          className={`px-3 py-1.5 rounded-lg border text-sm ${
            isDark ? 'bg-[#151515] border-[#333] text-white' : 'bg-white border-gray-200 text-gray-900'
          }`}
        />
        <span className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>To</span>
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          data-testid="daybook-to-date"
          className={`px-3 py-1.5 rounded-lg border text-sm ${
            isDark ? 'bg-[#151515] border-[#333] text-white' : 'bg-white border-gray-200 text-gray-900'
          }`}
        />
      </div>

      {/* Voucher Type Filter */}
      <div className="flex items-center gap-2">
        <Filter size={16} className={isDark ? 'text-slate-500' : 'text-gray-400'} />
        <select
          value={voucherTypeFilter}
          onChange={(e) => setVoucherTypeFilter(e.target.value)}
          data-testid="daybook-type-filter"
          className={`px-3 py-1.5 rounded-lg border text-sm ${
            isDark ? 'bg-[#151515] border-[#333] text-white' : 'bg-white border-gray-200 text-gray-900'
          }`}
        >
          <option value="all">All Vouchers</option>
          <option value="cash">Cash Only</option>
          <option value="bank">Bank Only</option>
          <option value="receipt">Receipts Only</option>
          <option value="payment">Payments Only</option>
        </select>
      </div>
      
      {/* Search */}
      <input
        type="text"
        placeholder="Search voucher, account..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        data-testid="daybook-search"
        className={`px-3 py-1.5 rounded-lg border text-sm w-44 ${
          isDark ? 'bg-[#151515] border-[#333] text-white placeholder:text-slate-600' : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'
        }`}
      />

      {/* Quick stats */}
      <div className={`flex items-center gap-3 ml-auto text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
        <span>CR: {voucherCounts.cashReceipt}</span>
        <span>CP: {voucherCounts.cashPayment}</span>
        <span>BR: {voucherCounts.bankReceipt}</span>
        <span>BP: {voucherCounts.bankPayment}</span>
      </div>
    </div>
  );

  return (
    <ReportLayout
      reportCode="DAY_BOOK"
      reportTitle="Day Book"
      reportSubtitle={`${fromDate} → ${toDate}`}
      reportIcon={<BookOpen size={20} />}
      data={filteredData}
      loading={loading}
      error={error}
      onRefresh={handleRefresh}
      theme={theme}
      summaryCards={summaryCards}
      filters={FiltersToolbar}
      showRowNumbers={true}
      pagination={{ page, pageSize, total: totalRows, totalPages }}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
    />
  );
};

export default DayBookScreen;
